import { workspaceDocumentFragments } from 'builder/Knowledge/queries/getDocument';
import { 
  queryObjectType,
  competencyItemGraphqlCache,
  competencyItemAttributes,
  knowledgeMapDetailInputStates,
  DESTINATION_ITEM_ID,
  ORIGIN_ITEM_ID,
} from 'constants/knowledgeMap';

/**
 * read competency document cache and update graphql cache
 * @param {string} competencyDocumentId
 * @param {string} competencyItemGraphqlUpdate
 * @param {Object} apolloClient - apollo client current instance.
 * @param {Object} competencyItemResponse - competencyDocumentItem response.
 * @param {Object} inputArgumentObject - contains input arguments
 */
export const updateCompetencyDocumentGraphqlCache = (
  competencyDocumentId,
  competencyItemGraphqlUpdate,
  apolloClient,
  competencyItemResponse,
  inputArgumentObject = {},
) => {
  const { 
    competencyTree,
    associationObject,
  } = inputArgumentObject;
  const { document: documentFragment } = workspaceDocumentFragments;
  const { DOCUMENT } = queryObjectType;
  const id = `${DOCUMENT}:${competencyDocumentId}`;
  const {
    COMPETENCY_ITEM_LIST_UPDATE,
    COMPETENCY_ITEM_DELETE,
    COMPETENCY_ITEM_LIST_REPLACE,
    COMPETENCY_ITEM_ASSOCIATION_REPLACE,
    COMPETENCY_ITEM_DELETE_ALL,
  } = competencyItemGraphqlCache;

  const competencyDocumentCache = apolloClient.readFragment({
    id,
    fragment: documentFragment,
  });
  
  const competencyItemsCache = competencyDocumentCache
      && competencyDocumentCache.items;
  
  if(!competencyItemsCache) {
    return;
  }

  if (competencyItemGraphqlUpdate === COMPETENCY_ITEM_LIST_UPDATE) {
    updateCompetencyDocumentItemList(
      competencyItemResponse,
      competencyItemsCache
    );
  } else if (competencyItemGraphqlUpdate === COMPETENCY_ITEM_DELETE) {
    deleteCompetencyDocumentItem(
      competencyTree,
      competencyItemResponse,
      competencyItemsCache
    );
  } else if (competencyItemGraphqlUpdate === COMPETENCY_ITEM_LIST_REPLACE) {
    replaceCompetencyDocumentItemInList(
      competencyItemResponse,
      competencyItemsCache
    );
  } else if (
    competencyItemGraphqlUpdate === COMPETENCY_ITEM_ASSOCIATION_REPLACE
  ) {
    replaceCompetencyDocumentAssociationList(
      competencyItemResponse,
      competencyItemsCache,
      associationObject
    );
  } else if (competencyItemGraphqlUpdate === COMPETENCY_ITEM_DELETE_ALL) {
    competencyDocumentCache.items.edges = [];
  } else {
    updateCompetencyDocumentAssociationList(
      competencyItemResponse,
      competencyItemsCache
    );
  }
  
  apolloClient.writeFragment({
    id,
    fragment: documentFragment,
    data: {
      ...competencyDocumentCache,
      __typename: competencyDocumentCache.__typename,
    },
  });
};

/** iterate response, create/append document items to cache
 * @param {Object} competencyItemResponse - competencyDocumentItem response.
 * @param {Object} competencyItemsCache
 */
export const updateCompetencyDocumentItemList = (competencyItemResponse, competencyItemsCache) => {
  const { DOCUMENT_ITEM_EDGE } = queryObjectType;

  Object.keys(competencyItemResponse.data).forEach((documentItemKey) => {
    const documentItem = createDocumentItem(competencyItemResponse, documentItemKey);
    competencyItemsCache.edges.push({node: documentItem, __typename: DOCUMENT_ITEM_EDGE});
  });
};

/** iterate response, create/append association items to cache
 * @param {Object} competencyItemResponse - competencyDocumentItem response.
 * @param {Object} competencyItemsCache
 */
export const updateCompetencyDocumentAssociationList = (competencyItemResponse, competencyItemsCache) => {
  Object.keys(competencyItemResponse.data).forEach((documentItemKey) => {
    const associationItemNode = createAssociationItemNode(competencyItemResponse, documentItemKey);
    const { originItemId: childItemId, destinationItemId: parentItemId } = associationItemNode.node;
    createAssociationEdges(competencyItemsCache, childItemId, associationItemNode, false);
    createAssociationEdges(competencyItemsCache, parentItemId, associationItemNode, true);
  });
};

/** iterate response, delete item from cache
 * @param {Object} competencyTree
 * @param {Object} competencyItemResponse - competencyDocumentItem response.
 * @param {Object} competencyItemsCache
 */
export const deleteCompetencyDocumentItem = (competencyTree, competencyItemResponse, competencyItemsCache) => {
  const { competencyDocumentItemDelete } = competencyItemResponse.data;
  const { documentItem } = competencyDocumentItemDelete;
  const { ITEM_ID_ATTRIBUTE, ORIGIN_ITEM_ID_ATTRIBUTE } = competencyItemAttributes;
  const { id: documentItemId } = documentItem;

  const deleteCompetencyItemIndex = findCompetencyItemNodeIndex(
    competencyItemsCache, 
    documentItemId,
    ITEM_ID_ATTRIBUTE
  );
  
  const [ parentItemId ] = competencyTree.getAllParentsByDeepChildId(documentItem.id);
  if(parentItemId) {
    const parentItem = findCompetencyItemNode(competencyItemsCache, parentItemId);
    
    const associationFromCache = parentItem?.associationsFrom;
    if(associationFromCache) {
      const associationIndex = findCompetencyItemNodeIndex(
        associationFromCache, 
        documentItemId, 
        ORIGIN_ITEM_ID_ATTRIBUTE
      );
      associationFromCache.edges.splice(associationIndex, 1);
    }
  }
    
  competencyItemsCache.edges.splice(deleteCompetencyItemIndex, 1);
};

/**
 * 
 * @param {Object} competencyItemResponse 
 * @param {Object} competencyItemsCache 
 * 
 */

export const replaceCompetencyDocumentItemInList = (competencyItemResponse, competencyItemsCache) => {
  const { ABBREVIATEDSTATEMENT } = knowledgeMapDetailInputStates;
  Object.keys(competencyItemResponse.data).forEach((documentItemKey) => {
    const competencyDocumentItem = competencyItemResponse.data[documentItemKey];
    const { id: competencyDocumentItemId, [ABBREVIATEDSTATEMENT]:abbreviatedStatement } = competencyDocumentItem.documentItem;

    const cachedCompetencyDocumentItem = findCompetencyItemNode(competencyItemsCache, competencyDocumentItemId);
    cachedCompetencyDocumentItem[ABBREVIATEDSTATEMENT] = abbreviatedStatement;

  });
};

/**
 * 
 * @param {Object} competencyItemResponse 
 * @param {Object} competencyItemsCache 
 * @param {Object} associationObject
 *  
 */

export const replaceCompetencyDocumentAssociationList = (competencyItemResponse, competencyItemsCache, associationObject) => {
  Object.keys(competencyItemResponse.data).forEach((documentItemKey) => {
    const associationId = competencyItemResponse.data[documentItemKey].associationId;
    const parentId = associationObject[associationId];
    const knowledgeMapItemId = associationObject[parentId];

    const parentItem = findCompetencyItemNode(competencyItemsCache, parentId);
    const associationFromCache = parentItem.associationsFrom;
    const associationFromIndex = findCompetencyItemNodeIndex(
      associationFromCache, 
      knowledgeMapItemId, 
      ORIGIN_ITEM_ID,
    );
    associationFromCache.edges.splice(associationFromIndex, 1);
    
    const competencyItem = findCompetencyItemNode(competencyItemsCache, knowledgeMapItemId);
    const associationToCache = competencyItem.associationsTo;
    const associationToIndex = findCompetencyItemNodeIndex(
      associationToCache, 
      parentId, 
      DESTINATION_ITEM_ID,
    );
    associationToCache.edges.splice(associationToIndex, 1);
  });
};

/**
 * find index based on attribute and selected itemid 
 * @param {Object} competencyItemCache
 * @param {String} itemId
 * @param {String} itemAttribute
 * @returns {String} - matched item index.
 */
export const findCompetencyItemNodeIndex = (competencyItemCache, itemId, itemAttribute) => {
  const competencyDocumentItemNodeIndex = competencyItemCache.edges.findIndex(
    (item) => item.node[itemAttribute] === itemId
  );
  return competencyDocumentItemNodeIndex;
};


/**
 * find competency Item node
 * @param {Array} competencyItemList
 * @param {String} itemId
 * @returns {Object} - matched item.
 */
export const findCompetencyItemNode = (competencyItemList, itemId) => {
  const competencyDocumentItemNode = competencyItemList.edges.find((item) => item.node.id === itemId);
  return competencyDocumentItemNode?.node;
};

/**
 * create Association Item node
 * @param {Object} createCompetencyAssociationItemResponse
 * @param {String} documentItemKey
 * @returns {Object} - association node item.
 */
export const createAssociationItemNode = (createCompetencyAssociationItemResponse, documentItemKey) => {
  const { association: associationItem } = createCompetencyAssociationItemResponse.data[documentItemKey];
  const { ITEM_ASSOCIATION } = queryObjectType;
  associationItem.__typename = ITEM_ASSOCIATION;
  return { node: associationItem };
};

/**
 * create Association Edges
 * @param {Array} competencyItemCacheList
 * @param {String} itemId
 * @param {Object} associationItemNode
 * @param {boolean} isParentItem
 */
export const createAssociationEdges = (competencyItemCacheList, itemId, associationItemNode, isParentItem) => {
  const { ITEM_ASSOCIATION_EDGE } = queryObjectType;
  const competencyDocumentItem = findCompetencyItemNode(competencyItemCacheList, itemId);
  const associationEdge = { ...associationItemNode, __typename: ITEM_ASSOCIATION_EDGE };
  if(isParentItem) {
    competencyDocumentItem.associationsFrom.edges.push(associationEdge);
  } else {
    competencyDocumentItem.associationsTo.edges.push(associationEdge);
  }
};

/**
 * create competency Item
 * @param {Object} createCompetencyItemResponse
 * @param {String} documentItemKey
 * @returns {Object} - documentItem.
 */
export const createDocumentItem = (createCompetencyItemResponse, documentItemKey) => {
  const { 
    DOCUMENT_ITEM,
    ITEM_ASSOCIATION_CONNECTION,
  } = queryObjectType;

  const documentItem = createCompetencyItemResponse.data[documentItemKey].documentItem;
  documentItem.associationsTo = { edges: [], __typename: ITEM_ASSOCIATION_CONNECTION};
  documentItem.associationsFrom = { edges: [], __typename: ITEM_ASSOCIATION_CONNECTION };
  documentItem.__typename = DOCUMENT_ITEM;
  return documentItem;
};
